import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { 
  Activity, Loader2, Building2, Play, Trash2, RefreshCw, 
  CheckCircle2, XCircle, AlertCircle, ChevronDown, ChevronRight,
  Copy, ExternalLink, User, Key, Building
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from '../config/api';

// =============================================================================
// UNIFIED SYSTEM HEALTH DASHBOARD
// Clean, organized, one-click testing with test org credentials
// =============================================================================

const SystemHealthDashboard = () => {
  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [testOrg, setTestOrg] = useState(() => {
    // Load from localStorage on init
    const saved = localStorage.getItem('healthDashboard_testOrg');
    return saved ? JSON.parse(saved) : null;
  });
  const [testDataCounts, setTestDataCounts] = useState(null);
  const [quickHealthStatus, setQuickHealthStatus] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // Save test org to localStorage when it changes
  useEffect(() => {
    if (testOrg) {
      localStorage.setItem('healthDashboard_testOrg', JSON.stringify(testOrg));
    } else {
      localStorage.removeItem('healthDashboard_testOrg');
    }
  }, [testOrg]);

  // Load initial data
  useEffect(() => {
    loadHealthStatus();
    loadTestDataCounts();
    loadExistingTestOrgs();
  }, []);

  const loadHealthStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/health/test-data/quick-check`);
      setQuickHealthStatus(response.data);
    } catch (error) {
      console.error('Failed to load health status:', error);
    }
  };

  const loadTestDataCounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/health/test-data/counts`);
      setTestDataCounts(response.data);
    } catch (error) {
      console.error('Failed to load test data counts:', error);
    }
  };

  const loadExistingTestOrgs = async () => {
    try {
      const response = await axios.get(`${API_URL}/health/test-data/test-orgs`);
      if (response.data.testOrgs && response.data.testOrgs.length > 0 && !testOrg) {
        // Use the most recent test org
        const latestOrg = response.data.testOrgs[0];
        if (latestOrg.adminUser) {
          setTestOrg({
            id: latestOrg.id,
            name: latestOrg.name,
            slug: latestOrg.slug,
            email: latestOrg.adminUser.email,
            password: latestOrg.adminUser.password
          });
        }
      }
    } catch (error) {
      console.error('Failed to load existing test orgs:', error);
    }
  };

  // Create test organization
  const createTestOrg = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/health/test-data/create-test-org?name_suffix=manual`);
      if (response.data.success) {
        setTestOrg({
          id: response.data.testOrg.id,
          name: response.data.testOrg.name,
          slug: response.data.testOrg.slug,
          email: response.data.testUser.email,
          password: response.data.testUser.password
        });
        toast.success('Test organization created!');
        loadTestDataCounts();
      }
    } catch (error) {
      toast.error('Failed to create test org: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Delete all test data
  const cleanupTestData = async () => {
    if (!window.confirm('Are you sure you want to delete ALL test data? This cannot be undone.')) {
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.delete(`${API_URL}/health/test-data/cleanup-all?confirm=true`);
      toast.success(`Deleted ${response.data.totalDeleted} test records`);
      setTestOrg(null);
      localStorage.removeItem('healthDashboard_testOrg');
      setTestResults(null);
      loadTestDataCounts();
    } catch (error) {
      toast.error('Failed to cleanup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Run all tests
  const runAllTests = async () => {
    setIsRunningTests(true);
    setTestResults(null);
    
    const results = {
      timestamp: new Date().toISOString(),
      sections: {},
      summary: { total: 0, passed: 0, failed: 0, errors: 0 }
    };

    // Only include WORKING PostgreSQL-based test endpoints
    // These are the ones that don't have MongoDB dependencies
    const testEndpoints = [
      // 🎯 BOOKING ENGINE V2 - BULLETPROOF TESTS (36 tests - PostgreSQL native)
      { name: '🎯 Booking Engine V2 - FULL AUDIT', endpoint: '/health/booking-engine-v2/full-audit', method: 'POST' },
      
      // 📊 DATA INTEGRITY AUDIT - Scan Real Data (DA-1 to DA-10)
      { name: '📊 Data Integrity Audit (DA-1 to DA-10)', endpoint: '/health/data-audit/run-all', method: 'GET' },
      
      // 🛡️ Roles Tab Audit (Comprehensive CRUD + Activity Logging)
      { name: 'Roles Comprehensive', endpoint: '/health/tests/roles-comprehensive', method: 'POST' },
      
      // 🛡️ Team Members Tab Audit (CRUD + Role Assignment + Login)
      { name: 'Team Members Comprehensive', endpoint: '/health/tests/team-members-comprehensive', method: 'POST' },
      
      // 📋 BOOKING SYSTEMS AUDIT (Comprehensive - 22 tests)
      { name: '📋 Booking Systems Audit', endpoint: '/health/test-suites/booking-audit', method: 'POST' },
      
      // 🔄 Booking Workflow Tests (Status, payments, confirmations - 10 tests)
      { name: '🔄 Booking Workflow', endpoint: '/health/test-suites/booking-workflow', method: 'POST' },
      
      // ⚡ Capacity & Conflicts (Double-booking, overflow detection - 6 tests)
      { name: '⚡ Capacity & Conflicts', endpoint: '/health/test-suites/capacity-conflicts', method: 'POST' },
      
      // 🌐 Service-Grid Widget API (Customer-facing + Admin services path - 8 tests)
      { name: '🌐 Service-Grid API', endpoint: '/health/test-suites/service-grid-api', method: 'POST' },
      
      // 🔧 Internal Booking API (Admin manual booking creation - 8 tests)
      { name: '🔧 Internal Booking API', endpoint: '/health/test-suites/internal-booking-api', method: 'POST' },
      
      // 📝 Internal Booking Flow (Pay Later, Calendar, Cron Jobs - 10 tests)
      { name: '📝 Internal Booking Flow', endpoint: '/health/test-suites/internal-booking-flow', method: 'POST' },
      
      // 🎯 Availability Accuracy (Timezone, slots, capacity)
      { name: '🎯 Availability Accuracy', endpoint: '/health/test-suites/availability-accuracy', method: 'POST' },
      
      // 💰 Revenue Accuracy (Fee tracking, calculations)
      { name: '💰 Revenue Accuracy', endpoint: '/health/test-suites/revenue-accuracy', method: 'POST' },
      
      // 🛡️ Assets Tab Audit (CRUD + Service Linking + Activity Logging)
      { name: 'Assets Comprehensive', endpoint: '/health/tests/assets-comprehensive', method: 'POST' },
      
      // 🛡️ Services Tab Audit (CRUD + Pricing + Scheduling + Activity Logging)
      { name: 'Services Comprehensive', endpoint: '/health/tests/services-comprehensive', method: 'POST' },
      
      // 🛡️ Schedule/Bookings Tab Audit (CRUD + Overbooking Prevention + Activity Logging)
      { name: 'Schedule Comprehensive', endpoint: '/health/tests/schedule-comprehensive', method: 'POST' },
      
      // 🛡️ Relations/CRM Tab Audit (Customer listing, search, export)
      { name: 'Relations Comprehensive', endpoint: '/health/tests/relations-comprehensive', method: 'POST' },
      
      // 🛡️ Flows/Embed Tokens Tab Audit (Token generation, validation, activity logging)
      { name: 'Flows Comprehensive', endpoint: '/health/test-suites/flows-v2', method: 'POST' },
      
      // ✅ Check-In Tab Audit (Settings, Scanner Sessions, QR Scan, Search)
      { name: 'Check-In Comprehensive', endpoint: '/health/test-suites/checkin-v2', method: 'POST' },
      
      // 💰 Discount Codes Tab Audit (CRUD, Validation, Usage Tracking, Export)
      { name: 'Discount Codes', endpoint: '/health/test-suites/discounts', method: 'POST' },
      
      // 📜 Waivers Comprehensive (Settings, Signing Flow, Device Fingerprint, Lookup)
      { name: 'Waivers Comprehensive', endpoint: '/health/test-suites/waivers-comprehensive', method: 'POST' },
      
      // 📝 Activity Log Tests (PostgreSQL v2 - Compliance & Auditing)
      { name: 'Activity Log', endpoint: '/health/test-suites/activity-log-v2', method: 'POST' },
      
      // 🛠️ Services Tests (Add-ons, Custom Questions, Dynamic Pricing, Shared Assets)
      { name: 'Services (Dynamic Pricing)', endpoint: '/health/test-suites/services-comprehensive', method: 'POST' },
      
      // 🚫 Overbooking Prevention (Critical - Prevents double-booking of assets)
      { name: 'Overbooking Prevention', endpoint: '/health/test-suites/overbooking-prevention', method: 'POST' },
      
      // 🔗 Service Sharing (Multiple services sharing same asset) - TODO: Needs PostgreSQL migration
      // { name: 'Service Sharing', endpoint: '/health/test-suites/service-sharing', method: 'POST' },
      
      // 💳 Widget Checkout (Payment flow, promo codes, add-ons)
      { name: 'Widget Checkout', endpoint: '/health/test-suites/widget-checkout-fixes', method: 'POST' },
      
      // 📊 Data Integrity (Referential integrity, orphaned records)
      { name: 'Data Integrity', endpoint: '/health/test-suites/data-integrity', method: 'POST' },
      
      // 📧 Email Notifications (Booking confirmations, reminders)
      { name: 'Email Notifications', endpoint: '/health/test-suites/email-notifications', method: 'POST' },
      
      // 🔔 Webhook Flow (External integrations, event delivery)
      { name: 'Webhook Flow', endpoint: '/health/test-suites/webhook-flow', method: 'POST' },
      
      // 🌐 Timezone Consistency (DST handling, cross-timezone bookings)
      { name: 'Timezone Consistency', endpoint: '/health/test-suites/timezone-consistency', method: 'POST' },
      
      // 🔗 Cross-Service Availability (Shared assets across services)
      { name: 'Cross-Service Availability', endpoint: '/health/test-suites/cross-service-availability', method: 'POST' },
      
      // Core PostgreSQL flows (these work!)
      { name: 'Signup Flow', endpoint: '/health/tests/signup-flow', method: 'POST' },
      { name: 'Login Flow', endpoint: '/health/tests/login-flow', method: 'POST' },
      { name: 'Booking Flow', endpoint: '/health/tests/booking-flow', method: 'POST' },
      
      // 🎯 Booking Accuracy Tests (TDD - Critical Money Path)
      { name: 'Booking Accuracy', endpoint: '/health/booking-accuracy/run-all', method: 'POST' },
      
      // 🎯 Internal Booking Tests (TDD - Same Engine as Widget)
      { name: 'Internal Booking', endpoint: '/health/internal-booking/run-all', method: 'POST' },
      
      // 📅 Calendar & Time Slots Polish Tests (TDD - UX Polish)
      { name: 'Calendar & Time Slots', endpoint: '/health/calendar-timeslots/run-all', method: 'POST' },
      
      // 🔗 Shared Asset Availability Tests (CRITICAL - Ensures correct capacity across services)
      { name: 'Shared Asset Availability', endpoint: '/health/tests/shared-asset-availability', method: 'POST' },
      
      // 🔄 Asset Capacity Sync Tests (When asset capacity changes, services update)
      { name: 'Asset Capacity Sync', endpoint: '/health/test-suites/asset-capacity-sync', method: 'POST', needsAuth: true },
      
      // 🌐 Timezone Handling Tests (Ensures DST and cross-timezone bookings work correctly)
      { name: 'Timezone Handling', endpoint: '/health/tests/timezone-handling', method: 'POST' },
      
      // 💰 Financials Tests (Revenue calculations, refunds, payouts)
      { name: 'Financials', endpoint: '/health/test-suites/financials', method: 'POST' },
      
      // 🛡️ Platform Admin Tests (Super admin functions)
      { name: 'Platform Admin', endpoint: '/health/test-suites/platform-admin', method: 'POST' },
      
      // Database health check
      { name: 'Database Health', endpoint: '/health/test-data/quick-check', method: 'GET' },
      
      // Test data counts
      { name: 'Test Data Status', endpoint: '/health/test-data/counts', method: 'GET' },
    ];

    for (const { name, endpoint, method } of testEndpoints) {
      try {
        const response = method === 'GET' 
          ? await axios.get(`${API_URL}${endpoint}`, { timeout: 120000 })
          : await axios.post(`${API_URL}${endpoint}`, {}, { timeout: 120000 });
        const data = response.data;
        
        // Extract tests from various response formats
        let tests = data.tests || data.results || [];
        
        // Handle quick-check format (it has 'checks' not 'tests')
        if (data.checks && !tests.length) {
          tests = Object.entries(data.checks).map(([key, val]) => ({
            name: key.replace(/_/g, ' '),
            status: val.status === '✅' ? 'passed' : val.status === 'ℹ️' ? 'passed' : 'failed',
            message: val.message || val.count
          }));
        }
        
        // Handle counts format
        if (data.breakdown && !tests.length) {
          tests = Object.entries(data.breakdown).map(([key, val]) => ({
            name: `${key.replace(/_/g, ' ')}: ${val}`,
            status: 'passed',
            message: `${val} records`
          }));
        }
        
        // Handle Calendar & Time Slots format (has 'suites' array with nested tests)
        if (data.suites && !tests.length) {
          tests = [];
          for (const suite of data.suites) {
            for (const test of (suite.tests || [])) {
              tests.push({
                name: `[${suite.testSuite}] ${test.name}`,
                status: test.status === 'info' ? 'passed' : test.status,
                message: test.actual || test.expected || ''
              });
            }
          }
        }
        
        // Handle Booking Accuracy format (tests have 'suite' field and different status values)
        // Also handle 'warning' and 'skipped' statuses
        const passed = tests.filter(t => 
          t.status === 'passed' || t.status === 'pass' || t.passed === true
        ).length;
        const failed = tests.filter(t => 
          t.status === 'failed' || t.status === 'fail' || t.passed === false
        ).length;
        const warnings = tests.filter(t => t.status === 'warning').length;
        const skipped = tests.filter(t => t.status === 'skipped').length;
        
        results.sections[name] = {
          status: failed === 0 && tests.length > 0 ? 'passed' : failed > 0 ? 'failed' : 'empty',
          total: tests.length,
          passed,
          failed,
          warnings,
          skipped,
          tests: tests.slice(0, 50) // Keep first 50 for display
        };
        
        results.summary.total += tests.length;
        results.summary.passed += passed;
        results.summary.failed += failed;
      } catch (error) {
        results.sections[name] = {
          status: 'error',
          error: error.response?.data?.detail || error.message,
          total: 0,
          passed: 0,
          failed: 0,
          tests: []
        };
        results.summary.errors++;
      }
    }

    setTestResults(results);
    setIsRunningTests(false);
    
    if (results.summary.failed === 0 && results.summary.errors === 0) {
      toast.success(`All ${results.summary.passed} tests passed!`);
    } else if (results.summary.errors > 0) {
      toast.warning(`${results.summary.passed} passed, ${results.summary.errors} errors`);
    } else {
      toast.error(`${results.summary.failed} tests failed`);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold">System Health Dashboard</h1>
            <p className="text-gray-500 text-sm">Production-grade testing with real API calls</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            onClick={loadHealthStatus} 
            variant="outline" 
            size="sm"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Status Bar */}
      {quickHealthStatus && (
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          {Object.entries(quickHealthStatus.checks || {}).map(([key, check]) => (
            <div key={key} className="flex items-center gap-2">
              <span className="text-lg">{check.status}</span>
              <span className="text-sm text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
      )}

      {/* Test Org Card - PROMINENT */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="h-6 w-6 text-blue-600" />
            <h2 className="text-lg font-semibold">Test Organization</h2>
          </div>
          
          <div className="flex items-center gap-2">
            {!testOrg && (
              <Button onClick={createTestOrg} disabled={isLoading} className="bg-blue-600 hover:bg-blue-700">
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Building2 className="h-4 w-4 mr-2" />}
                Create Test Org
              </Button>
            )}
            {testDataCounts?.totalTestRecords > 0 && (
              <Button onClick={cleanupTestData} variant="destructive" size="sm" disabled={isLoading}>
                <Trash2 className="h-4 w-4 mr-2" />
                Purge All Test Data ({testDataCounts.totalTestRecords})
              </Button>
            )}
          </div>
        </div>

        {testOrg ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {/* Org Slug / Shortname */}
            <div className="bg-white rounded-lg p-4 border border-blue-100">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                <Building className="h-3 w-3" />
                ORG SHORTNAME
              </div>
              <div className="flex items-center justify-between">
                <code className="text-lg font-mono font-bold text-blue-600">{testOrg.slug}</code>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(testOrg.slug)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Login Email */}
            <div className="bg-white rounded-lg p-4 border border-blue-100">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                <User className="h-3 w-3" />
                LOGIN EMAIL
              </div>
              <div className="flex items-center justify-between">
                <code className="text-sm font-mono text-gray-800 truncate">{testOrg.email}</code>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(testOrg.email)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Password */}
            <div className="bg-white rounded-lg p-4 border border-blue-100">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                <Key className="h-3 w-3" />
                PASSWORD
              </div>
              <div className="flex items-center justify-between">
                <code className="text-lg font-mono font-bold text-green-600">{testOrg.password}</code>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(testOrg.password)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No test organization created yet</p>
            <p className="text-sm">Click "Create Test Org" to get started</p>
          </div>
        )}

        {/* Quick Login Link */}
        {testOrg && (
          <div className="mt-4 pt-4 border-t border-blue-200">
            <a 
              href={`/login?org=${testOrg.slug}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              <ExternalLink className="h-4 w-4" />
              Open Login Page for {testOrg.slug}
            </a>
          </div>
        )}
      </div>

      {/* Run All Tests Button - BIG & PROMINENT */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Run All Tests</h2>
            <p className="text-gray-500 text-sm">Execute core PostgreSQL tests against real APIs</p>
          </div>
          
          <Button 
            onClick={runAllTests} 
            disabled={isRunningTests}
            size="lg"
            className="bg-green-600 hover:bg-green-700 text-lg px-8 py-6"
          >
            {isRunningTests ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Running Tests...
              </>
            ) : (
              <>
                <Play className="h-5 w-5 mr-2" />
                Run All Tests
              </>
            )}
          </Button>
        </div>

        {/* Test Results Summary */}
        {testResults && (
          <div className="mt-6 pt-6 border-t">
            {/* Summary Bar */}
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{testResults.summary.passed}</span>
                <span className="text-gray-500">passed</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <span className="text-2xl font-bold text-red-600">{testResults.summary.failed}</span>
                <span className="text-gray-500">failed</span>
              </div>
              {testResults.summary.errors > 0 && (
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  <span className="text-2xl font-bold text-orange-600">{testResults.summary.errors}</span>
                  <span className="text-gray-500">errors</span>
                </div>
              )}
              <div className="ml-auto text-sm text-gray-400">
                {new Date(testResults.timestamp).toLocaleString()}
              </div>
            </div>

            {/* Results by Section */}
            <div className="space-y-2">
              {Object.entries(testResults.sections).map(([name, section]) => (
                <div key={name} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleSection(name)}
                    className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 ${
                      section.status === 'passed' ? 'bg-green-50 border-l-4 border-l-green-500' : 
                      section.status === 'failed' ? 'bg-red-50 border-l-4 border-l-red-500' : 
                      section.status === 'empty' ? 'bg-gray-50 border-l-4 border-l-gray-300' :
                      'bg-orange-50 border-l-4 border-l-orange-500'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {section.status === 'passed' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : section.status === 'failed' ? (
                        <XCircle className="h-5 w-5 text-red-600" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-orange-600" />
                      )}
                      <span className="font-medium">{name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">
                        {section.passed}/{section.total} passed
                        {section.warnings > 0 && <span className="text-yellow-600 ml-1">({section.warnings} warnings)</span>}
                        {section.skipped > 0 && <span className="text-gray-400 ml-1">({section.skipped} skipped)</span>}
                      </span>
                      {expandedSections[name] ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </button>
                  
                  {expandedSections[name] && section.tests && section.tests.length > 0 && (
                    <div className="p-3 bg-white border-t">
                      <div className="space-y-1 max-h-80 overflow-y-auto">
                        {section.tests.map((test, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm py-1">
                            {(test.status === 'passed' || test.status === 'pass' || test.passed) ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                            ) : test.status === 'warning' ? (
                              <AlertCircle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                            ) : test.status === 'skipped' ? (
                              <span className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5 text-center">⏭️</span>
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              {/* Show suite name for Booking Accuracy tests */}
                              {test.suite && (
                                <span className="inline-block text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded mr-2">{test.suite}</span>
                              )}
                              <span className="text-gray-700">{test.name || test.id}</span>
                              {(test.message || test.actual || test.expected) && (
                                <span className="text-gray-400 text-xs block mt-0.5 truncate">
                                  — {test.message || test.actual || test.expected}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {section.error && (
                    <div className="p-3 bg-orange-50 border-t text-sm text-orange-700">
                      Error: {section.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Test Data Stats */}
      {testDataCounts && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4">Test Data Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(testDataCounts.breakdown || {}).map(([table, count]) => (
              <div key={table} className="text-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className="text-2xl font-bold text-gray-700">{count}</div>
                <div className="text-xs text-gray-500 capitalize">{table.replace(/_/g, ' ')}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-4">
            Test data is prefixed with <code className="bg-gray-100 px-1 rounded">zztest-</code> for easy identification
          </p>
        </div>
      )}
    </div>
  );
};

export default SystemHealthDashboard;
